import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  MessageSquare, Plus, ArrowLeft, Clock, Users, Loader2,
} from 'lucide-react';
import { projectId } from '../../utils/supabase/info';
import { toast } from 'sonner';
import { MessageComposer } from './MessageComposer';
import { MessageTypeBadge, SenderRoleBadge } from './MessageTypeBadge';
import type {
  EngagementThread, EngagementMessage, MessageClassification,
} from './types';

interface EngagementThreadViewProps {
  engagementId: string;
  engagementName: string;
  accessToken: string;
  currentUser: any;
  configItems?: Array<{ id: string; name: string }>;
  onClose?: () => void;
  /** Pre-select a specific thread on mount (deep-link support) */
  initialThreadId?: string;
}

export function EngagementThreadView({
  engagementId,
  engagementName,
  accessToken,
  currentUser,
  configItems = [],
  onClose,
  initialThreadId,
}: EngagementThreadViewProps) {
  const [threads, setThreads] = useState<EngagementThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<EngagementThread | null>(null);
  const [messages, setMessages] = useState<EngagementMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newConfigItemId, setNewConfigItemId] = useState<string>('none');
  const [filterConfigItemId, setFilterConfigItemId] = useState<string>('all');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [initialThreadResolved, setInitialThreadResolved] = useState(false);

  const baseUrl = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

  // Load threads
  useEffect(() => {
    loadThreads();
  }, [engagementId, filterConfigItemId]);

  // Auto-select thread from initialThreadId after threads load
  useEffect(() => {
    if (initialThreadId && threads.length > 0 && !initialThreadResolved && !selectedThread) {
      const match = threads.find((t) => t.id === initialThreadId);
      if (match) {
        setSelectedThread(match);
      }
      setInitialThreadResolved(true);
    }
  }, [initialThreadId, threads, initialThreadResolved, selectedThread]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ userId: currentUser.id });
      if (filterConfigItemId && filterConfigItemId !== 'all') {
        params.set('configItemId', filterConfigItemId);
      }
      const response = await fetch(
        `${baseUrl}/engagement-messaging/${engagementId}/threads?${params}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setThreads(data.threads || []);
      }
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load messages for selected thread
  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
    }
  }, [selectedThread?.id]);

  const loadMessages = async (threadId: string) => {
    try {
      setLoadingMessages(true);
      const response = await fetch(
        `${baseUrl}/engagement-messaging/${engagementId}/threads/${threadId}/messages?userId=${currentUser.id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        // Scroll to bottom
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Create new thread
  const handleCreateThread = async (data: {
    content: string;
    classification: MessageClassification;
    urgentReason?: string;
  }) => {
    if (!newSubject.trim()) {
      toast.error('Please enter a thread subject');
      return;
    }

    const ciId = newConfigItemId === 'none' ? null : newConfigItemId;
    const ciName = ciId ? configItems.find((ci) => ci.id === ciId)?.name : null;

    try {
      const response = await fetch(
        `${baseUrl}/engagement-messaging/${engagementId}/threads`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subject: newSubject.trim(),
            configItemId: ciId,
            configItemName: ciName,
            engagementName,
            createdBy: currentUser.id,
            createdByName: currentUser.name || currentUser.email,
            initialMessage: data.content,
            classification: data.classification,
            urgentReason: data.urgentReason,
            senderEmail: currentUser.email,
            senderRole: currentUser.role || currentUser.activeRole,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setShowNewThread(false);
        setNewSubject('');
        setNewConfigItemId('none');
        setSelectedThread(result.thread);
        setMessages([result.message]);
        await loadThreads();
        toast.success('Thread created');
      } else {
        const err = await response.json();
        toast.error(err.error || 'Failed to create thread');
      }
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to create thread');
    }
  };

  // Send reply in thread
  const handleSendReply = async (data: {
    content: string;
    classification: MessageClassification;
    urgentReason?: string;
  }) => {
    if (!selectedThread) return;

    try {
      const response = await fetch(
        `${baseUrl}/engagement-messaging/${engagementId}/threads/${selectedThread.id}/messages`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: data.content,
            classification: data.classification,
            urgentReason: data.urgentReason,
            senderId: currentUser.id,
            senderName: currentUser.name || currentUser.email,
            senderEmail: currentUser.email,
            senderRole: currentUser.role || currentUser.activeRole,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setMessages((prev) => [...prev, result.message]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        // Refresh thread list to update previews
        loadThreads();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  const formatFullTime = (ts: string) => {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ---- Thread List View ----
  if (!selectedThread && !showNewThread) {
    return (
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <h2 className="text-base font-semibold text-foreground">
                Messages
              </h2>
              <p className="text-xs text-muted-foreground">{engagementName}</p>
            </div>
          </div>
          <Button size="sm" onClick={() => setShowNewThread(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            New Thread
          </Button>
        </div>

        {/* Config Item Filter */}
        {configItems.length > 0 && (
          <div className="px-5 py-3 border-b bg-slate-50/50">
            <Select value={filterConfigItemId} onValueChange={setFilterConfigItemId}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Filter by config item..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Threads</SelectItem>
                {configItems.map((ci) => (
                  <SelectItem key={ci.id} value={ci.id}>
                    {ci.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Thread List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : threads.length === 0 ? (
            <div className="py-16 text-center">
              <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground font-medium">No threads yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Start a new thread to begin communication
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {threads.map((thread) => (
                <button
                  key={thread.id}
                  onClick={() => setSelectedThread(thread)}
                  className={`w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors ${
                    thread.hasUnread ? 'bg-blue-50/40' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {thread.hasUnread && (
                        <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1" />
                      )}
                      <span className={`text-sm truncate ${thread.hasUnread ? 'font-semibold' : 'font-medium'}`}>
                        {thread.subject}
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {formatTime(thread.lastMessageAt)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-1.5">
                    <MessageTypeBadge classification={thread.lastMessageClassification} size="sm" />
                    {thread.configItemName && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50">
                        {thread.configItemName}
                      </Badge>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {thread.lastMessagePreview}
                  </p>

                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {thread.messageCount}
                    </span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {thread.participantIds.length}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    );
  }

  // ---- New Thread View ----
  if (showNewThread) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center gap-3 px-5 py-4 border-b">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowNewThread(false)}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-base font-semibold">New Thread</h2>
            <p className="text-xs text-muted-foreground">{engagementName}</p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 border-b">
          <div>
            <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Subject
            </Label>
            <Input
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Thread subject..."
              className="text-sm"
            />
          </div>

          {configItems.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Config Item (optional)
              </Label>
              <Select value={newConfigItemId} onValueChange={setNewConfigItemId}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Select config item..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Engagement-level)</SelectItem>
                  {configItems.map((ci) => (
                    <SelectItem key={ci.id} value={ci.id}>
                      {ci.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex-1" />

        <MessageComposer
          onSend={handleCreateThread}
          placeholder="Write the first message in this thread..."
          disabled={!newSubject.trim()}
        />
      </div>
    );
  }

  // ---- Thread Messages View ----
  return (
    <div className="flex flex-col h-full bg-white">
      {/* Thread Header */}
      <div className="px-5 py-3.5 border-b">
        <div className="flex items-center gap-3 mb-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setSelectedThread(null);
              setMessages([]);
            }}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold truncate">{selectedThread?.subject}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-muted-foreground">{engagementName}</span>
              {selectedThread?.configItemName && (
                <>
                  <span className="text-[10px] text-muted-foreground">/</span>
                  <span className="text-[10px] text-muted-foreground">
                    {selectedThread.configItemName}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="px-5 py-4 space-y-4">
          {loadingMessages ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              No messages in this thread
            </div>
          ) : (
            messages.map((msg) => {
              const isOwnMessage = msg.senderId === currentUser.id;
              const isSystemMsg = msg.isSystem;

              if (isSystemMsg) {
                return (
                  <div key={msg.id} className="flex justify-center py-2">
                    <div className="bg-slate-100 text-slate-500 text-xs px-3 py-1.5 rounded-full max-w-md text-center">
                      {msg.content}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={msg.id}
                  className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] ${
                      isOwnMessage
                        ? 'bg-primary/5 border border-primary/15 rounded-tl-xl rounded-tr-sm rounded-bl-xl rounded-br-xl'
                        : 'bg-slate-50 border border-slate-200 rounded-tl-sm rounded-tr-xl rounded-bl-xl rounded-br-xl'
                    } px-4 py-3`}
                  >
                    {/* Sender info */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-foreground">
                        {msg.senderName}
                      </span>
                      <SenderRoleBadge roleLabel={msg.senderRoleLabel} size="sm" />
                      <MessageTypeBadge classification={msg.classification} size="sm" />
                    </div>

                    {/* Urgent reason */}
                    {msg.classification === 'urgent' && msg.urgentReason && (
                      <div className="bg-rose-50 border border-rose-100 rounded px-2.5 py-1.5 mb-2">
                        <span className="text-[10px] font-medium text-rose-600 uppercase tracking-wider">
                          Urgency Reason
                        </span>
                        <p className="text-xs text-rose-700 mt-0.5">
                          {msg.urgentReason}
                        </p>
                      </div>
                    )}

                    {/* Content */}
                    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                      {msg.content}
                    </p>

                    {/* Timestamp */}
                    <div className="flex items-center gap-1.5 mt-2">
                      <Clock className="h-3 w-3 text-muted-foreground" />
                      <span className="text-[10px] text-muted-foreground">
                        {formatFullTime(msg.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Composer */}
      <MessageComposer
        onSend={handleSendReply}
        placeholder="Reply to this thread..."
        compact
      />
    </div>
  );
}